import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';

import { gameCreateCommand } from '../../../components/utilities';
import GameService from '../../../../../services/game.service';
import GameTagButtonList from '../../../games/tabs/overview/tagButtonList';
import GameTagMenu from '../../../games/tabs/overview/tagMenu';
import GameExportToEdits from '../../../games/tabs/overview/exportEdits';
import { XmlDataFilterTeamOverview } from '../../../components/xmldatateam';
import Alert from '../../../../../common/alert';
import SharedVideoPlayer from '../../../components/SharedVideoPlayer';
import { ActionData, TagToActionData, normalizeTagData, isGoalTag } from '../../../components/common';
import { TAG_KEYS } from '../../../games/tabs/overview/tagKeys';

const TeamOverview = ({ games, gameIds, teamname, teamId, t }) => {
    const { user: currentUser } = useSelector((state) => state.auth);
    const [showAlert, setShowAlert] = useState(false);
    const [showResultAlert, setShowResultAlert] = useState(false);
    const [downState, setDownState] = useState('');

    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    useEffect(() => {
        if (showResultAlert) {
            const timer = setTimeout(() => {
                setShowResultAlert(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showResultAlert]);

    const [values, setValues] = useState({
        isOur: true,
        clickEventName: ''
    });

    // tagIndex stays the DISPATCH key so the branches below keep matching.
    // tagLabel is the button's identity, which is what marks it selected;
    // tagSide fixes the perspective for buttons that carry one.
    const [tagLabel, setTagLabel] = useState('');
    const [tagSide, setTagSide] = useState(null);
    const [tagIndex, setTagIndex] = useState('');
    const [loadData, setLoadData] = useState(false);
    const [exportHudl, setExportHudl] = useState(false);
    const [exportEditOpen, setExportEditOpen] = useState(false);
    const [exportList, setExportList] = useState([]);
    const [gameList, setGameList] = useState([]);
    const [videoOpen, setVideoOpen] = useState(false);
    const [playData, setPlayData] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleChangeTeam = (flag) => {
        setValues({ ...values, isOur: flag });
        setTagIndex(-1);
    };

    const handleShowPopover = (button) => (e) => {
        const idx = button.tag;

        setTagLabel(button.label);
        setTagSide(button.side ?? null);
        if (gameIds.length === 0) {
            window.alert("No selected games. Please click 'Select Games' button to select.");
            return;
        }

        setTagIndex(idx);
        setMenuAnchorEl(e.currentTarget);
    };

    const handleClickView = () => {
        setMenuAnchorEl(null);
        setLoading(true);
        setValues({ ...values, clickEventName: 'video' });
        setLoadData(true);
    };

    const handleClickHudl = () => {
        setMenuAnchorEl(null);
        setLoading(true);
        setValues({ ...values, clickEventName: 'sportcode' });
        setLoadData(true);
    };

    const handleClickEdits = () => {
        setMenuAnchorEl(null);
        setLoading(true);
        setValues({ ...values, clickEventName: 'my_edits' });
        setLoadData(true);
    };

    const getPlayTagList = async (func) => {
        func.then(async (res) => {
            setLoadData(false);
            setLoading(false);

            // Goal clips are shots only; assists are also tagged result=Goal, so filter them out.
            if (TagToActionData[tagIndex] === 'Goal') res = res.filter(isGoalTag);

            if (values.clickEventName === 'render') {
                setShowAlert(true);
                const result = await gameCreateCommand(res, (t(tagIndex) || tagIndex), gameList, gameIds);
                setShowResultAlert(true);
                setDownState(result);
            } else if (values.clickEventName === 'sportcode') {
                setExportList(normalizeTagData(res, { teamId }));
                setExportHudl(true);
            } else if (values.clickEventName === 'my_edits') {
                setExportList(normalizeTagData(res, { teamId }));
                setExportEditOpen(true);
            } else if (values.clickEventName === 'video') {
                const transformedData = normalizeTagData(res, { teamId });
                if (transformedData.length > 0 && gameList.length > 0) {
                    setPlayData(transformedData);
                    setVideoOpen(true);
                } else if (transformedData.length === 0) {
                    window.alert('No clips found for this action.');
                }
            }
        }).catch((error) => {
            console.error('Error loading data:', error);
            setLoadData(false);
            setLoading(false);
        });
    };

    useEffect(() => {
        // Only make API calls when we have valid data AND a valid teamId (not -1)
        if (loadData && gameIds.length > 0 && teamId && teamId > 0) {
            const gameIdsStr = gameIds.join(',');

            // Check if tag has ActionData mapping - use unified getGamePlayerTags/getOpponentTags
            const actionKey = TagToActionData[tagIndex];
            if (actionKey && ActionData[actionKey]) {
                const actionData = ActionData[actionKey];
                const useOrLogic = actionData.useOrLogic ? 1 : null;

                if (tagSide ? tagSide === 'our' : values.isOur) {
                    getPlayTagList(
                        GameService.getGamePlayerTags(
                            currentUser.id,
                            teamId,
                            null, // all players
                            gameIdsStr,
                            actionData.action_id,
                            actionData.action_type_id,
                            actionData.action_result_id,
                            null, null, null, null, null,
                            useOrLogic
                        )
                    );
                } else {
                    getPlayTagList(
                        GameService.getOpponentTags(
                            currentUser.id,
                            teamId, // our team id for opponent query
                            null, // all players
                            gameIdsStr,
                            actionData.action_id,
                            actionData.action_type_id,
                            actionData.action_result_id,
                            null, null, null, null, null
                        )
                    );
                }
            }
            // Buttons migrated to the generic /game/tagclips endpoint - checked before the
            // per-button possession branches below so a migrated key always takes this path.
            else if (TAG_KEYS[tagIndex]) {
                getPlayTagList(GameService.getTagClips(TAG_KEYS[tagIndex], teamId, gameIdsStr, tagSide ? tagSide === 'our' : values.isOur));
            }
            // Complex possession-based tags - use old s4u_new_ functions
            else if (tagIndex === 'All Offensive Possessions') {
                if (values.isOur) getPlayTagList(GameService.getTeamOffensivePossession(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentOffensivePossession(teamId, gameIdsStr));
            } else if (tagIndex === 'All Defensive Possessions') {
                if (values.isOur) getPlayTagList(GameService.getTeamDefensivePossession(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentDefensivePossession(teamId, gameIdsStr));
            } else if (tagIndex === 'Build Up Goalkeeper') {
                if (values.isOur) getPlayTagList(GameService.getTeamBuildupGoalkeeper(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentBuildupGoalkeeper(teamId, gameIdsStr));
            } else if (tagIndex === 'Build Up Own Half') {
                if (values.isOur) getPlayTagList(GameService.getTeamBuildonDefensiveHalf(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentBuildonDefensiveHalf(teamId, gameIdsStr));
            } else if (tagIndex === 'Build Up Opponent Half') {
                if (values.isOur) getPlayTagList(GameService.getTeamBuildOnOffensiveHalf(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentBuildOnOffensiveHalf(teamId, gameIdsStr));
            } else if (tagIndex === 'Counter-Attacks') {
                if (values.isOur) getPlayTagList(GameService.getTeamCounterAttack(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentCounterAttack(teamId, gameIdsStr));
            } else if (tagIndex === 'Started From Interception') {
                if (values.isOur) getPlayTagList(GameService.getTeamInterception(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentInterception(teamId, gameIdsStr));
            } else if (tagIndex === 'Started From Tackle') {
                if (values.isOur) getPlayTagList(GameService.getTeamTackle(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentTackle(teamId, gameIdsStr));
            } else if (tagIndex === 'Started From Throw In') {
                if (values.isOur) getPlayTagList(GameService.getTeamThrowIn(teamId, gameIdsStr));
                else getPlayTagList(GameService.getOpponentThrowIn(teamId, gameIdsStr));
            } else {
                // Unknown tag
                setLoadData(false);
                window.alert('This action type is not available yet.');
            }
        }
    }, [tagIndex, loadData, teamId]);

    useEffect(() => {
        // Filter games that are selected AND have valid video URLs
        // Convert to numbers for comparison since gameIds may contain strings or numbers
        const numericGameIds = gameIds.map((id) => Number(id));
        const filteredGames = games.filter((item) =>
            numericGameIds.includes(Number(item.id)) &&
            item.video_url &&
            item.video_url.toLowerCase() !== 'no video'
        );
        setGameList(filteredGames);
    }, [games, gameIds]);

    return (
        <Box sx={{ width: '100%', background: '#f8fafc', overflowY: 'auto', padding: '12px 8px', maxHeight: '79vh', minHeight: '77vh' }}>
                        {showAlert && <Alert text={'Render started!'} />}
            {showResultAlert && <Alert text={downState} />}
            <GameTagButtonList
                t={t}
                selectedTag={tagLabel}
                onShow={handleShowPopover}
                isTeams={true}
                loadingTag={loading ? tagIndex : null}
            />
            <GameTagMenu
                t={t}
                anchor={menuAnchorEl}
                onClose={() => setMenuAnchorEl(null)}
                onView={handleClickView}
                onHudl={handleClickHudl}
                onEdits={handleClickEdits}
            />
            <SharedVideoPlayer
                t={t}
                tagName={tagLabel}
                open={videoOpen}
                onClose={(flag) => {
                    setVideoOpen(false);
                    if (flag) setRefresh((r) => !r);
                }}
                video_url={gameList}
                tagList={playData}
            />
            <GameExportToEdits t={t} open={exportEditOpen} onClose={() => setExportEditOpen(false)} tagList={exportList} isTeams={true} />
            {exportHudl && <XmlDataFilterTeamOverview games={gameList} tagList={exportList} isOur={tagSide ? tagSide === 'our' : values.isOur} tag_name={(t(tagIndex) || tagIndex)} team_name={teamname} setExportXML={setExportHudl} />}
        </Box>
    );
};

export default TeamOverview;
